/**
 * Forced password-reset screen shown after first login on a temporary password.
 * Routed to by the auth guard when the user must change their password before
 * accessing the app.
 *
 * On success it re-fetches the user (getMe) so the must_change_password flag is
 * cleared in context, then redirects to the role home.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword, getMe } from '../services/apiService';
import { validateNewPassword, PASSWORD_REQUIREMENTS_TEXT } from '../utils/passwordPolicy';
import cegLogo from '../assets/CEG Logo.svg';

function IconEye() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

const HOME_BY_ROLE = {
  founder: '/dashboard',
  eir: '/eir',
  ceg: '/ceg',
  admin: '/admin',
};

/** Mandatory password-change form for users still on a temporary password. */
export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [current, setCurrent] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (newPwd !== confirm) { setError('New passwords do not match'); return; }
    const pwError = validateNewPassword(newPwd);
    if (pwError) { setError(pwError); return; }
    if (newPwd === current) { setError('New password must be different from your temporary password'); return; }
    setLoading(true);
    try {
      await changePassword(current, newPwd);
      const updated = await getMe();
      setUser(updated);
      navigate(HOME_BY_ROLE[updated.role] || '/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoWrap}>
          <img src={cegLogo} alt="CEG" style={s.logoImg} />
        </div>
        <h1 style={s.title}>Set Your Password</h1>
        <p style={s.subtitle}>
          Your account was set up with a temporary password.{'\n'}Please choose a new password to continue.
        </p>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label} htmlFor="current">Temporary Password</label>
            <div style={s.inputWrap}>
              <input
                id="current"
                type={showPasswords ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                style={s.input}
                placeholder="Your temporary password"
                required
                autoFocus
              />
              <button type="button" style={s.eyeBtn} onClick={() => setShowPasswords((v) => !v)}>
                {showPasswords ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor="newPwd">New Password</label>
            <div style={s.inputWrap}>
              <input
                id="newPwd"
                type={showPasswords ? 'text' : 'password'}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                style={s.input}
                placeholder={PASSWORD_REQUIREMENTS_TEXT}
                required
              />
              <button type="button" style={s.eyeBtn} onClick={() => setShowPasswords((v) => !v)}>
                {showPasswords ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label} htmlFor="confirm">Confirm New Password</label>
            <div style={s.inputWrap}>
              <input
                id="confirm"
                type={showPasswords ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={s.input}
                placeholder="Repeat new password"
                required
              />
              <button type="button" style={s.eyeBtn} onClick={() => setShowPasswords((v) => !v)}>
                {showPasswords ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
          </div>

          {error && <p style={s.error}>{error}</p>}

          <button type="submit" style={s.submitBtn} disabled={loading}>
            {loading ? 'Saving…' : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--color-mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-6)' },
  card: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-modal)', padding: 'var(--space-10)', width: '100%', maxWidth: 420 },
  logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-5)' },
  logoImg: { width: 80, height: 80, objectFit: 'contain' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 28, fontWeight: 800, color: 'var(--color-forest)', textAlign: 'center', marginBottom: 'var(--space-2)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.65, textAlign: 'center', marginBottom: 'var(--space-8)', lineHeight: 1.6, whiteSpace: 'pre-line' },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  field: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  input: { flex: 1, padding: '10px 40px 10px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--color-forest)', outline: 'none', width: '100%', boxSizing: 'border-box' },
  eyeBtn: { position: 'absolute', right: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-forest)', opacity: 0.45, padding: 0, display: 'flex', alignItems: 'center' },
  error: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', textAlign: 'center', margin: 0 },
  submitBtn: { marginTop: 'var(--space-2)', padding: '12px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer' },
};
